export interface QueryParams {
  [key: string]: string;
}

export interface UriParamsBuilderApiInterface {
  replaceVar: (variableName: string, value: string) => UriParamsBuilderApiInterface;
  replaceRegex: (regexExp: RegExp, value: string) => UriParamsBuilderApiInterface;
  replaceMulti(
    params: string[],
    getReplacementValue: (paramName: string) => string,
  ): UriParamsBuilderApiInterface;
  build: () => string;
}

const getQueryParams = (url: string): QueryParams => {
  try {
    const urlObject = new URL(url);
    const searchParams = new URLSearchParams(urlObject.search);
    return Object.fromEntries(searchParams.entries());
  } catch {
    return {};
  }
};

const getQueryParamByName = (url: string, paramName: string): undefined | string =>
  getQueryParams(url)[paramName];

const getQueryParamByRegex = (url: string, paramRegex: RegExp): undefined | string => {
  const params: QueryParams = getQueryParams(url);
  const param: string = Object.keys(params).find((paramKey) => paramRegex.test(paramKey)) ?? '';
  return params[param];
};

const attachQueryParams = (url: string, params: string): string => {
  try {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(params);

    searchParams.forEach((value, key) => {
      urlObj.searchParams.append(key, value);
    });

    return urlObj.toString();
  } catch {
    return url;
  }
};

const LinkParamsBuilder = (link: string): UriParamsBuilderApiInterface => {
  let builtLink: string = link;

  const api: UriParamsBuilderApiInterface = {
    replaceRegex(regex: RegExp, value: string): UriParamsBuilderApiInterface {
      builtLink = builtLink.replace(regex, value);
      return api;
    },
    replaceVar(variableName: string, value: string): UriParamsBuilderApiInterface {
      const regexExp = new RegExp(`{{${variableName}}}`, 'g');
      return api.replaceRegex(regexExp, value);
    },
    replaceMulti(
      params: string[],
      getReplacementValue: (paramName: string) => string,
    ): UriParamsBuilderApiInterface {
      params.forEach((param): void => {
        api.replaceVar(param, getReplacementValue(param));
      });

      return api;
    },
    build(): string {
      return builtLink;
    },
  };

  return api;
};

const replaceAllTemplateVarsWithParams = (str: string, uriWithData: string): string => {
  const placeholderValue = /{{([^{}]*)}}/g;
  const brackets = /{{|}}/gm;
  const varNames = str
    ?.match(placeholderValue)
    ?.map((placeholder: string) => placeholder.replace(brackets, ''));
  const builder = LinkParamsBuilder(str);

  varNames?.forEach((name: string) => {
    const varValue = getQueryParamByName(uriWithData, name) || '';
    builder.replaceVar(name, varValue);
  });

  return builder.build();
};

const replaceVariables = (text: string, uriWithData: string): string => {
  // GDPR_CONSENT is an URL-safe base64-encoded Transparency & Consent string. Only meaningful if gdpr=1.
  // For more information about GDPR visit:
  // https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20Consent%20string%20and%20vendor%20list%20formats%20v2.md
  const gdprRegex = /(\${|{{|%)GDPR(}}|}|%)/gi;
  const gdprConsentRegex = /(\${|{{|%)GDPR_CONSENT[_0-9]*(}}|}|%)/gi;
  const deliveryRegex = /(\${|{{|%)delivery(}}|}|%)/gi;
  const ifaRegex = /(\${|{{|%)ifa(}}|}|%)/gi;
  const deviceAdvidRegex = /(\${|{{|%)device_advid(}}|}|%)/gi;
  const excludedParams = [
    'ref',
    'referrer',
    'timestamp',
    'wmid',
    'gdpr',
    'pid',
    'gdpr_consent',
    'delivery',
    'ifa',
    'device_advid',
  ];
  const params: string[] = Object.keys(getQueryParams(uriWithData)).filter(
    (param) => !excludedParams.includes(param),
  );

  return (
    LinkParamsBuilder(text)
      .replaceVar('ref', getQueryParamByName(uriWithData, 'ref') ?? '')
      .replaceVar('referrer', getQueryParamByName(uriWithData, 'ref') ?? '')
      .replaceVar('timestamp', `${Date.now()}`)
      .replaceVar('wmid', getQueryParamByName(uriWithData, 'werbemittel-id') ?? '')
      .replaceVar('pid', getQueryParamByName(uriWithData, 'flaechen-id') ?? '')
      .replaceVar('externalLinkMakro', getQueryParamByName(uriWithData, 'ext-link') ?? '')
      .replaceVar('extVastMakro', getQueryParamByName(uriWithData, 'ext-vast-url') ?? '')
      // unique TCP tracking params for all linkOuts
      .replaceRegex(gdprRegex, getQueryParamByRegex(uriWithData, /gdpr/gi) ?? '')
      .replaceRegex(gdprConsentRegex, getQueryParamByName(uriWithData, 'gdpr_consent') ?? '')
      .replaceRegex(deliveryRegex, getQueryParamByName(uriWithData, 'delivery') ?? '')
      .replaceRegex(deviceAdvidRegex, getQueryParamByName(uriWithData, 'ifa') ?? '')
      .replaceRegex(ifaRegex, getQueryParamByName(uriWithData, 'ifa') ?? '')
      // replace any other query param
      .replaceMulti(params, (paramName) => getQueryParamByName(uriWithData, paramName) ?? '')
      .build()
  );
};

const replaceUrlVariables = (text: string | undefined): string => {
  if (!text) {
    return '';
  }
  return replaceVariables(text, window.location.href);
};

const isLocalhost = (): boolean =>
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(window.location.hostname);

const isF11Host = (): boolean => {
  const url =
    window.location !== window.parent.location ? document.referrer : document.location.hostname;
  return (
    url.includes('factor-eleven') ||
    url.includes('ad-preview') ||
    url.includes('ad-production-stage')
  );
};

const getDeviceModeInfo = (): string | undefined => {
  return (
    getQueryParamByName(window.location.href, 'device_mode') ||
    getQueryParamByName(window.location.href, 'f11_device_mode')
  );
};

export const UriUtils = {
  getQueryParams,
  getQueryParamByName,
  getQueryParamByRegex,
  getDeviceModeInfo,
  attachQueryParams,
  LinkParamsBuilder,
  replaceVariables,
  replaceAllTemplateVarsWithParams,
  replaceUrlVariables,
  isLocalhost,
  isF11Host,
};
